import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/integrations/supabase/types";

type SubscriptionPlan = Database["public"]["Enums"]["subscription_plan"];
type SubscriptionStatus = Database["public"]["Enums"]["subscription_status"];

export interface Subscription {
  id: string;
  plan: SubscriptionPlan;
  status: SubscriptionStatus;
  trial_ends_at: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  scheduled_plan: SubscriptionPlan | null;
}

export const useSubscription = () => {
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  // Unique channel name per mount prevents "add callback after subscribe()" error
  // when React StrictMode double-invokes effects or fast re-renders occur.
  const channelId = useRef(`subscription-updates-${crypto.randomUUID()}`);

  // Real-time listener: invalidate when admin changes our subscription row
  useEffect(() => {
    if (!user) return;
    const channel = supabase
      .channel(channelId.current)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "subscriptions",
          // filter by restaurant rows belonging to this user is complex via
          // realtime, so we listen broadly and invalidate — the query itself
          // only fetches the user's own restaurant's subscription (via RLS).
        },
        () => {
          queryClient.invalidateQueries({ queryKey: ["subscription", user.id] });
        }
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [user, queryClient]);

  const query = useQuery({
    queryKey: ["subscription", user?.id],
    enabled: !authLoading && !!user,
    queryFn: async (): Promise<Subscription | null> => {
      if (!user) return null;

      // First get the user's restaurant
      const { data: restaurant, error: restaurantError } = await supabase
        .from("restaurants")
        .select("id, is_complimentary")
        .eq("user_id", user.id)
        .maybeSingle() as { data: { id: string; is_complimentary: boolean } | null; error: unknown };

      if (restaurantError) {
        console.error("[subscription] failed to load restaurant", restaurantError);
        return null;
      }

      if (!restaurant) return null;

      // Complimentary accounts bypass payment and receive the current
      // top-tier limits/permissions.
      if (restaurant.is_complimentary) {
        return {
          id: "complimentary",
          plan: "operacao" as SubscriptionPlan,
          status: "active" as SubscriptionStatus,
          trial_ends_at: null,
          current_period_end: null,
          cancel_at_period_end: false,
          scheduled_plan: null,
        };
      }

      // First try to find an active/past_due paid subscription
      const { data: paidSub, error: paidSubError } = await supabase
        .from("subscriptions")
        .select("id, plan, status, trial_ends_at, current_period_end, cancel_at_period_end, scheduled_plan")
        .eq("restaurant_id", restaurant.id)
        .in("status", ["active", "past_due"])
        .maybeSingle();

      if (paidSubError) {
        console.error("[subscription] failed to load paid subscription", paidSubError);
        return null;
      }

      if (paidSub) return paidSub;

      // No trial flow — user is free until they subscribe
      return null;
    },
    retry: false,
    staleTime: 60 * 1000, // 1 minute (realtime keeps it fresh on admin changes)
  });

  const subscription = query.data ?? null;
  const status = subscription?.status ?? null;
  const isActive = status === "active";
  const isPastDue = status === "past_due";
  const isTrialing = status === "trialing";
  const isFree = subscription === null;

  // Check if trial is still valid
  const trialEndsAt = subscription?.trial_ends_at ? new Date(subscription.trial_ends_at) : null;
  const isTrialExpired = isTrialing && trialEndsAt ? trialEndsAt <= new Date() : false;
  const isTrialActive = isTrialing && !isTrialExpired;

  // Determine effective plan: during an active trial, give current top-tier access.
  const plan = isActive || isPastDue
    ? subscription!.plan
    : isTrialActive
      ? "operacao" as const
      : "free_trial" as const;

  // Days remaining in trial
  const trialDaysRemaining = isTrialActive && trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24)))
    : 0;

  const cancelAtPeriodEnd = subscription?.cancel_at_period_end ?? false;
  const scheduledPlan = subscription?.scheduled_plan ?? null;
  const periodEnd = subscription?.current_period_end ? new Date(subscription.current_period_end) : null;
  // Access expired when cancel_at_period_end is set and the period has passed
  const isCancelledAtPeriodEnd = cancelAtPeriodEnd && periodEnd ? periodEnd <= new Date() : false;

  return {
    subscription,
    plan,
    status,
    isActive,
    isPastDue,
    isTrialing,
    isTrialActive,
    isTrialExpired,
    trialEndsAt,
    trialDaysRemaining,
    isFree,
    cancelAtPeriodEnd,
    scheduledPlan,
    isCancelledAtPeriodEnd,
    isLoading: authLoading || query.isLoading,
  };
};
